import {
  type UseFormReturn,
  type FieldValues,
  type Path,
  type PathValue,
} from "react-hook-form";
import { z } from "zod";

/**
 * 最小値最大値を設定しない場合の関数
 * @param rangeErrorMessage 範囲開始値<範囲終了値となっていない場合のエラーメッセージ
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const makeRangeSchema = (rangeErrorMessage: string) => {
  return z
    .object({
      min: z.number().nullable(),
      max: z.number().nullable(),
    })
    .refine(
      (args) => {
        // min<=maxを確認。
        const { min, max } = args;
        if (min === null || max === null) return true;
        return min <= max;
      },
      {
        message: rangeErrorMessage,
      }
    );
};

/**
 * 最小値最大値を設定する場合の関数
 * @param min 設定可能な最小値
 * @param max 設定可能な最大値
 * @param rangeErrorMessage 範囲開始値<範囲終了値となっていない場合のエラーメッセージ
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const makeRangeSchemaWithRestrictMinMax = (
  min: number,
  max: number,
  rangeErrorMessage: string
) => {
  const minErrorMessage = `${min}以上の数値を入力してください`;
  const maxErrorMessage = `${max}以下の数値を入力してください`;

  /**
   * 最小値のチェック。
   * @param {string} value
   * @returns {boolean}
   */
  const checkMin = (value: number | null): boolean => {
    if (value === null) return true;
    return min <= value;
  };

  /**
   * 最大値のチェック。
   * @param {string} value
   * @returns {boolean}
   */
  const checkMax = (value: number | null): boolean => {
    if (value === null) return true;
    return max >= value;
  };

  return z
    .object({
      min: z
        .number()
        .nullable()
        .refine((value) => checkMin(value), minErrorMessage)
        .refine((value) => checkMax(value), maxErrorMessage),
      max: z
        .number()
        .nullable()
        .refine((value) => checkMin(value), minErrorMessage)
        .refine((value) => checkMax(value), maxErrorMessage),
    })
    .refine(
      (args) => {
        // min<=maxを確認。
        const { min, max } = args;
        if (min === null || max === null) return true;
        return min <= max;
      },
      {
        message: rangeErrorMessage,
      }
    );
};

/**
 * 再帰的にreact-hook-formのsetValueを使って値を更新する
 * @param form react-hook-formのUseFormReturnオブジェクト
 * @param basePath 基本パス
 * @param values 設定する値
 */
export const setValuesRecursively = <T extends FieldValues>(
  form: UseFormReturn<T>,
  basePath: string,
  values: Record<string, unknown>
): void => {
  for (const key in values) {
    const value = values[key];
    const path = `${basePath}.${key}`;

    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      setValuesRecursively(form, path, value as Record<string, unknown>);
    } else {
      form.setValue(path as Path<T>, value as PathValue<T, Path<T>>);
    }
  }
};
